import { useState, useEffect, useRef } from "react";
import { Link2, ClipboardType, ScanLine, Sparkles, Loader2, FileWarning, ArrowRight, Bot, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import StudioResult from "./StudioResult";
import PrecheckCard from "./PrecheckCard";

export const MODELS = [
  { id: "claude", label: "Claude Sonnet 5.5", hint: "Best writing" },
  { id: "gpt", label: "GPT 5.6", hint: "Fast & sharp" },
  { id: "gemini", label: "Gemini 3.1 Pro", hint: "Deep reasoning" },
  { id: "gemini-flash", label: "Gemini 3 Flash", hint: "Fastest" },
];

export default function NewApplication({ baseCv, onNeedCv, onCreated }) {
  const [tab, setTab] = useState(() => (localStorage.getItem("pending_jd") ? "paste" : "link"));
  const [url, setUrl] = useState(() => localStorage.getItem("pending_url") || "");
  const [jd, setJd] = useState(() => localStorage.getItem("pending_jd") || "");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [model, setModel] = useState("claude");
  const [scraping, setScraping] = useState(false);
  const [tailoring, setTailoring] = useState(false);
  const [result, setResult] = useState(null);
  const [precheck, setPrecheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [askOverride, setAskOverride] = useState(false);
  const checkedFor = useRef("");
  const debounceRef = useRef(null);
  const skipCompanyReset = useRef(false);

  const companyRef = useRef(company);
  companyRef.current = company;

  const runPrecheck = async (text, co = companyRef.current) => {
    setChecking(true);
    try {
      const { data } = await api.post("/jobs/precheck", { jd_text: text, company: co });
      checkedFor.current = text;
      setPrecheck(data);
      if (data.company) setCompany((c) => c || data.company);
      return data;
    } catch {
      setPrecheck(null);
      return null;
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    setPrecheck(null);
    setAskOverride(false);
    const text = jd.trim();
    let co = companyRef.current;
    if (checkedFor.current && checkedFor.current.slice(0, 60) !== text.slice(0, 60)) {
      if (skipCompanyReset.current) skipCompanyReset.current = false;
      else { co = ""; companyRef.current = ""; setCompany(""); }
    }
    if (text.length < 150) return;
    const t = setTimeout(() => runPrecheck(text, co), 900);
    debounceRef.current = t;
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jd]);

  useEffect(() => {
    if (localStorage.getItem("pending_jd") || localStorage.getItem("pending_url")) {
      localStorage.removeItem("pending_jd");
      localStorage.removeItem("pending_url");
      toast.success("We loaded the job you pasted — review and tailor it.");
    }
  }, []);

  if (!baseCv) {
    return (
      <div className="af-fade-up">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">New Application</h1>
        <div className="mt-8 rounded-2xl border border-warning/30 bg-warning-soft p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-xl bg-warning-soft border border-warning/30 flex items-center justify-center mb-4">
            <FileWarning className="w-6 h-6 text-warning" />
          </div>
          <h2 className="font-display text-xl font-bold">Upload your master CV first</h2>
          <p className="text-body mt-2 mb-5">We need a baseline résumé to tailor for each job.</p>
          <button
            data-testid="go-upload-cv-button"
            onClick={onNeedCv}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand text-onbrand font-semibold hover:bg-brand-hover transition-colors duration-200"
          >
            Upload Master CV <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const scrape = async () => {
    if (!url.trim()) return toast.error("Paste a job posting URL");
    setScraping(true);
    try {
      const { data } = await api.post("/scrape", { url: url.trim() });
      skipCompanyReset.current = true;
      setJd(data.jd_text || "");
      setCompany(data.company || "");
      setRole(data.role || "");
      setTab("paste");
      toast.success("Job description extracted — review it below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't scrape that page. Paste the JD manually.");
      setTab("paste");
    } finally {
      setScraping(false);
    }
  };

  const tailor = async (override = false) => {
    if (jd.trim().length < 40) return toast.error("Add the full job description first");
    let check = precheck;
    clearTimeout(debounceRef.current);
    if (override !== true && checkedFor.current !== jd.trim()) check = await runPrecheck(jd.trim());
    if (check?.flagged && override !== true) {
      setAskOverride(true);
      return toast.warning("This job looks suspicious. Check the warning above.");
    }
    setAskOverride(false);
    setTailoring(true);
    setResult(null);
    try {
      const { data } = await api.post("/applications/tailor", { jd_text: jd, jd_url: url, model });
      onCreated?.();
      // Tailoring runs as a background job (can take up to ~90s) — poll until done.
      const appId = data.id;
      const started = Date.now();
      while (Date.now() - started < 360000) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: doc } = await api.get(`/applications/${appId}`);
        if (doc.processing_status === "done") {
          setResult(doc);
          onCreated?.();
          toast.success(`Tailored — ${doc.ats_score}% ATS match`);
          return;
        }
        if (doc.processing_status === "failed") {
          throw new Error(doc.error || "Tailoring failed");
        }
      }
      throw new Error("Tailoring timed out. Please try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Tailoring failed");
    } finally {
      setTailoring(false);
    }
  };

  const reset = () => { setResult(null); setJd(""); setUrl(""); setCompany(""); setRole(""); };

  if (result) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-mono2 text-xs uppercase tracking-widest text-brand mb-1">Tailored application</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{result.role} · {result.company}</h1>
          </div>
          <button
            data-testid="tailor-another-button"
            onClick={reset}
            className="px-4 py-2 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors duration-200"
          >
            + New
          </button>
        </div>
        <StudioResult app={result} onEmailed={onCreated} />
      </div>
    );
  }

  return (
    <div className="af-fade-up">
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">New Application</h1>
      <p className="text-body mt-1 mb-7">Add a job. We'll check the post, show how well it fits your CV, and tailor your real experience to it.</p>

      {/* Tabs */}
      <div className="inline-flex p-1 rounded-lg bg-surface border border-line mb-5">
        <button
          data-testid="tab-link"
          onClick={() => setTab("link")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${tab === "link" ? "bg-brand text-onbrand" : "text-body hover:text-ink"}`}
        >
          <Link2 className="w-4 h-4" /> Job link
        </button>
        <button
          data-testid="tab-paste"
          onClick={() => setTab("paste")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${tab === "paste" ? "bg-brand text-onbrand" : "text-body hover:text-ink"}`}
        >
          <ClipboardType className="w-4 h-4" /> Paste JD
        </button>
      </div>

      {tab === "link" && (
        <div className="rounded-xl border border-line bg-surface p-5 mb-5">
          <label className="text-sm text-body mb-2 block">Job posting URL</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="job-link-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://boards.company.com/jobs/senior-engineer"
              className="flex-1 bg-canvas border border-line-strong rounded-lg px-4 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors"
            />
            <button
              data-testid="scrape-job-button"
              onClick={scrape}
              disabled={scraping}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-60"
            >
              {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
              {scraping ? "Scraping…" : "Scrape job"}
            </button>
          </div>
          <p className="text-xs text-faint mt-2">Some sites block bots (LinkedIn etc.) — if scraping fails, just paste the JD.</p>
        </div>
      )}

      <div className="rounded-xl border border-line bg-surface p-5 mb-5">
        <label className="text-sm text-body mb-2 block">Job description {(company || role) && <span className="text-brand">· {[role, company].filter(Boolean).join(" @ ")}</span>}</label>
        <textarea
          data-testid="jd-text-area"
          value={jd}
          onChange={(e) => setJd(e.target.value)}
          placeholder="Paste the full job description here (responsibilities, requirements, skills)…"
          rows={10}
          className="w-full bg-canvas border border-line-strong rounded-lg px-4 py-3 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors resize-y font-mono2 leading-relaxed"
        />
        <div className="text-xs text-faint mt-1.5 font-mono2">{jd.length} chars</div>
      </div>

      {/* Model + action */}
      <div className="mb-4">
        <PrecheckCard checking={checking} data={precheck} askOverride={askOverride} onOverride={() => tailor(true)} jdText={jd.trim()} company={company} onCompanyChange={setCompany} onReported={() => setPrecheck((p) => ({ ...p, you_reported: true }))} />
      </div>
      <div className="rounded-xl border border-line bg-surface p-5">
        <div className="flex items-center gap-2 mb-3">
          <Bot className="w-4 h-4 text-brand" />
          <span className="text-sm text-body">AI engine</span>
        </div>
        <div className="flex flex-wrap gap-3 mb-5">
          {MODELS.map((m) => (
            <button
              key={m.id}
              data-testid={`model-${m.id}`}
              onClick={() => setModel(m.id)}
              className={`text-left px-4 py-3 rounded-lg border transition-colors duration-200 ${model === m.id ? "border-brand bg-brand-soft ring-1 ring-brand" : "border-line hover:border-brand/40"}`}
            >
              <div className="text-sm font-semibold flex items-center gap-1.5">
                {model === m.id && <CheckCircle2 className="w-4 h-4 text-brand" data-testid={`model-${m.id}-selected`} />}
                {m.label}
              </div>
              <div className="text-xs text-faint">{m.hint}</div>
            </button>
          ))}
        </div>
        <button
          data-testid="analyze-jd-button"
          onClick={() => tailor()}
          disabled={tailoring || checking}
          className="w-full sm:w-auto flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-xl bg-brand text-onbrand font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-60"
        >
          {tailoring ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
          {tailoring ? "Agent is rewriting your CV to 95%+ ATS…" : "Tailor & analyze"}
        </button>
      </div>
    </div>
  );
}
